import { hashKey, MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { ApiError } from '@/shared/api/errors';
import { endSession, SESSION_END_REASONS, sessionQueryKey } from '@/shared/session/currentUser';

/**
 * Query client with the defaults this application wants.
 *
 * It is created per provider instance instead of at module scope so each test gets a
 * clean cache and nothing leaks between them.
 *
 * Every failed request, query or mutation, passes by `watchSessionEnd`: a 401 whose reason
 * ends the session (`second_factor_required`, contract v10.45) sends the person to the
 * door from whatever they clicked, with the reason for the door to say.
 */
export function createQueryClient(): QueryClient {
  let client: QueryClient | null = null;
  const watch = (error: unknown, fromSession = false) => {
    if (client) watchSessionEnd(client, error, fromSession);
  };
  client = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => watch(error, query.queryHash === sessionQueryHash),
    }),
    mutationCache: new MutationCache({ onError: (error) => watch(error) }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        // Coming back to the tab asks again for what is older than `staleTime`. Víctor
        // and Ernesto keep the app open all day in a tab: without this, whatever changed
        // meanwhile (a mailbox registered in the panel, a colleague's correction) only
        // showed after a reload. Forms are safe: the line editor keeps its edits while
        // `dirty`, and a refetch with the same data keeps the same object (structural
        // sharing), so no effect runs again.
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => {
          // A 401 or a 404 will not fix itself by asking again.
          if (error instanceof ApiError && (error.isUnauthorized || error.status === 404)) {
            return false;
          }
          return failureCount < 2;
        },
      },
    },
  });
  return client;
}

const sessionQueryHash = hashKey(sessionQueryKey);

/** The 401 that closes the session for a reason the door says (`SESSION_END_REASONS`). */
export function watchSessionEnd(client: QueryClient, error: unknown, fromSession = false): void {
  if (error instanceof ApiError && error.isUnauthorized && error.reason !== null) {
    if (SESSION_END_REASONS.has(error.reason)) endSession(client, error.reason, fromSession);
  }
}
